import { create } from 'zustand';
import { readUrlState, writeUrlState } from './urlState';
import type { AppMode, AtlasBasemap, AtlasLens, FilterState, UrlState } from '../data/types';

interface AppStore extends UrlState {
  setMode: (mode: AppMode) => void;
  setNode: (nodeId?: string, fromHistory?: boolean) => void;
  setCompare: (nodeId?: string) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  togglePin: (nodeId: string) => void;
  togglePathRelation: (relationId: string) => void;
  setPresentation: (storyId: string, stepIndex?: number) => void;
  setStep: (stepIndex: number) => void;
  setProfile: (profile: UrlState['profile']) => void;
  clearHistory: () => void;
  setAtlasLens: (atlasLens: AtlasLens) => void;
  setAtlasBasemap: (atlasBasemap: AtlasBasemap) => void;
  setAtlasYear: (atlasYear: number) => void;
  setAtlasSite: (atlasSiteId?: string) => void;
  setAtlasTour: (atlasTourId: string, atlasStepIndex?: number) => void;
  setAtlasStep: (atlasStepIndex: number) => void;
}

const initial = readUrlState();

function sync(state: AppStore): void {
  const { setMode, setNode, setCompare, setFilters, resetFilters, togglePin, togglePathRelation, setPresentation, setStep, setProfile, clearHistory, setAtlasLens, setAtlasBasemap, setAtlasYear, setAtlasSite, setAtlasTour, setAtlasStep, ...urlState } = state;
  void setMode; void setNode; void setCompare; void setFilters; void resetFilters; void togglePin; void togglePathRelation; void setPresentation; void setStep; void setProfile; void clearHistory;
  void setAtlasLens; void setAtlasBasemap; void setAtlasYear; void setAtlasSite; void setAtlasTour; void setAtlasStep;
  writeUrlState(urlState);
}

export const useAppStore = create<AppStore>((set, get) => ({
  ...initial,
  setMode: (mode) => {
    set({ mode });
    sync(get());
  },
  setNode: (nodeId, fromHistory = false) => {
    set((state) => {
      const historyIds = nodeId && !fromHistory && nodeId !== state.nodeId
        ? [nodeId, ...state.historyIds.filter((id) => id !== nodeId)].slice(0, 20)
        : state.historyIds;
      return { nodeId, historyIds };
    });
    sync(get());
  },
  setCompare: (compareId) => {
    set({ compareId });
    sync(get());
  },
  setFilters: (filters) => {
    set((state) => ({ filters: { ...state.filters, ...filters } }));
    sync(get());
  },
  resetFilters: () => {
    set((state) => ({ filters: { ...state.filters, search: '', nodeTypes: [], relationTypes: [], confidence: [], sourceIds: [], mediaOnly: false, from: undefined, to: undefined } }));
    sync(get());
  },
  togglePin: (nodeId) => {
    set((state) => ({ pinnedIds: state.pinnedIds.includes(nodeId) ? state.pinnedIds.filter((id) => id !== nodeId) : [...state.pinnedIds, nodeId].slice(-12) }));
    sync(get());
  },
  togglePathRelation: (relationId) => {
    set((state) => ({ pathRelationIds: state.pathRelationIds.includes(relationId) ? state.pathRelationIds.filter((id) => id !== relationId) : [...state.pathRelationIds, relationId] }));
    sync(get());
  },
  setPresentation: (storyId, stepIndex = 0) => {
    set({ mode: 'presentation', storyId, stepIndex });
    sync(get());
  },
  setStep: (stepIndex) => {
    set({ stepIndex });
    sync(get());
  },
  setProfile: (profile) => {
    set({ profile });
    sync(get());
  },
  clearHistory: () => {
    set({ historyIds: [] });
    sync(get());
  },
  setAtlasLens: (atlasLens) => {
    set({ atlasLens });
    sync(get());
  },
  setAtlasBasemap: (atlasBasemap) => {
    set({ atlasBasemap });
    sync(get());
  },
  setAtlasYear: (atlasYear) => {
    set({ atlasYear: Math.min(2026, Math.max(1760, Math.floor(atlasYear))) });
    sync(get());
  },
  setAtlasSite: (atlasSiteId) => {
    set({ atlasSiteId });
    sync(get());
  },
  setAtlasTour: (atlasTourId, atlasStepIndex = 0) => {
    set({ atlasLens: 'flight', atlasTourId, atlasStepIndex });
    sync(get());
  },
  setAtlasStep: (atlasStepIndex) => {
    set({ atlasStepIndex });
    sync(get());
  },
}));
